/**
 * Tax figures read off the copy of a Stripe tax calculation stored on
 * `project.checkout.stripeTaxCalculation`. Pure — no Vue, no Firebase.
 *
 * WHY: `$root.totals()` used to compute tax as `taxCalculation.tax_amount_exclusive / 100` with
 * no guard. Production project KX1ApYJF4sBZQTw5aAcD stored a TRUNCATED copy of its calculation —
 * only { id, tax_date, expires_at, line_items } — so that expression evaluated to NaN and NaN
 * propagated into `total` and `balance`.
 *
 * That is not a display glitch. ProjectHeaderPane renders
 *   project.invoiceDate ? (totals(project, true).balance ? 'No Payment Made' : 'Payment Complete')
 * and NaN is falsy, so an unpaid $3,178.64 order reported itself as PAID while its Order Summary
 * showed "NaN" for taxes and total.
 *
 * The per-line-item `amount_tax` values are the same figures Stripe used to compute the top-level
 * total, so a missing `tax_amount_exclusive` is recoverable rather than merely survivable. The
 * matching server-side helper lives in formfactories-api/stripeTaxCalculation.ts; the API's
 * invoice builders REFUSE a truncated calculation outright (guessing tax rates on an invoice is
 * worse than refusing), but totals() feeds display everywhere and has to return a number.
 */

function roundToCents (num) {
  return (+(Math.round(+(+num * 100)) * 0.01)).toFixed(2)
}

/**
 * The exclusive tax in CENTS, or null when the calculation carries no usable tax figure at all.
 *
 * @param {object|null|undefined} taxCalculation a stored Stripe tax calculation
 * @returns {number|null}
 */
export function stripeTaxAmountExclusive (taxCalculation) {
  if (!taxCalculation || typeof taxCalculation !== 'object') return null
  if (Number.isFinite(taxCalculation.tax_amount_exclusive)) return taxCalculation.tax_amount_exclusive

  const taxed = []
  const lineItems = taxCalculation.line_items?.data
  if (Array.isArray(lineItems)) {
    lineItems.forEach(item => {
      if (Number.isFinite(item?.amount_tax)) taxed.push(item.amount_tax)
    })
  }
  if (Number.isFinite(taxCalculation.shipping_cost?.amount_tax)) {
    taxed.push(taxCalculation.shipping_cost.amount_tax)
  }

  if (!taxed.length) return null
  return taxed.reduce((a, b) => a + b, 0)
}

/**
 * The tax for a project, in DOLLARS. Always a finite number.
 *
 * Prefers the Stripe calculation (frozen at checkout, so it does not drift with the subtotal),
 * falls back to the project's flat `taxRate`, and finally to 0 — never NaN.
 *
 * @param {object} args
 * @param {object} [args.taxCalculation] stored Stripe tax calculation, if any
 * @param {object} [args.taxRate] flat rate fallback, `{ amount }` as a decimal fraction
 * @param {number} [args.taxableSubtotal] subtotal the flat rate applies to
 * @param {number} [args.taxReversalAmount] tax already reversed, subtracted from the result
 * @returns {number}
 */
export function projectTax ({ taxCalculation, taxRate, taxableSubtotal, taxReversalAmount } = {}) {
  let tax = 0

  const stripeTax = stripeTaxAmountExclusive(taxCalculation)
  if (stripeTax !== null) {
    tax = stripeTax / 100
  } else if (taxRate?.amount) {
    // A truncated calculation reaches here too: better the flat rate than a NaN.
    const taxValue = +roundToCents((+taxableSubtotal * +taxRate.amount * 10000).toFixed() / 10000)
    if (!isNaN(taxValue)) tax = taxValue
  }

  tax -= taxReversalAmount || 0
  return isNaN(tax) ? 0 : tax
}
